import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Copy, Plus, Trash } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TimePicker } from "@/components/ui/timepicker";
import { useEffect } from "react";
import { useCourses } from "@/lib/courses";
import { Switch } from "@/components/ui/switch";
import { type ReactElement } from 'react';
import { format } from "date-fns";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useCommunication } from "@/lib/CommunicationWrapper";
//import { refreshClassTimes } from "@/lib/googlesync";
///import { refreshMicrosoftClassTimes } from "@/lib/microsoftsync";
import { Checkbox } from "@/components/ui/checkbox";
import * as React from "react";

const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

type SchedulePeriod = {
    day: number,
    start: number,
    end: number,
}

const ScheduleEditorCourse = ({ course, onClick }: { course: any, onClick: () => void }) => {

    if (!course) return null;

    const schedule: SchedulePeriod[] = course?.schedule || [];

    const render = useMemo(() => {
        if (schedule.length === 0) return "No times added";
        return schedule.map((day: SchedulePeriod) => {
            return days[day.day];
        }).join(", ");
    }, [schedule]);

    return (
        <div className="cursor-pointer hover:bg-theme-1 transition-all duration-200 rounded-lg p-[10px] flex items-center justify-between w-full" onClick={onClick}>
            <div>
                <div className="flex items-center gap-[10px]">
                    <div className="size-[12px] rounded-full" style={{ backgroundColor: course.color }}></div>
                    <h3 className="font-semibold text-theme-0">{course.code !== "" ? course.code : course.default}</h3>
                </div>
                <p className="text-sm text-theme-1">{render}</p>
            </div>
            <ChevronRight className="h-[16px] w-[16px] text-theme-1" />
        </div>
    )
}

const ScheduleTimeRange = ({ initStart, initEnd, handleChange, handleDelete, showTrash }: { initStart: number, initEnd: number, handleChange: (changes: any) => void, handleDelete: () => void, showTrash: boolean }) => {

    const [start, setStart] = useState<number>(initStart);
    const [end, setEnd] = useState<number>(initEnd);

    const { settings: options } = useCommunication();

    const hr24 = options.hr24;

    useEffect(() => {
        handleChange({ start });
    }, [start]);

    useEffect(() => {
        handleChange({ end });
    }, [end]);

    const today = new Date();
    const startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), start / 60, start % 60);
    const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), end / 60, end % 60);

    return (
        <div className="flex items-center gap-[10px]">
            <Popover>
                <PopoverTrigger asChild>
                    <button className="border border-theme rounded-md py-[5px] px-[10px] text-sm">{format(startDate, hr24 ? "HH:mmaaa" : "h:mmaaa")}</button>
                </PopoverTrigger>
                <PopoverContent>
                    <TimePicker value={start} onChange={(time) => setStart(time)} format={hr24 ? "24h" : "12h"} />
                </PopoverContent>
            </Popover>
            <span>-</span>
            <Popover>
                <PopoverTrigger asChild>
                    <button className="border border-theme rounded-md py-[5px] px-[10px] text-sm">{format(endDate, hr24 ? "HH:mmaaa" : "h:mmaaa")}</button>
                </PopoverTrigger>
                <PopoverContent>
                    <TimePicker value={end} onChange={(time) => setEnd(time)} format={hr24 ? "24h" : "12h"} />
                </PopoverContent>
            </Popover>
            {showTrash && (
                <button className="rounded-md p-[5px] hover:bg-theme-2" onClick={handleDelete}>
                    <Trash className="h-[16px] w-[16px] text-theme-0" />
                </button>
            )}
        </div>
    )
}

const CopyTimesPopover = ({ sourceDayIndex, onCopy, children }: { sourceDayIndex: number, onCopy: (targetDays: number[]) => void, children: React.ReactNode }) => {
    const [selectedDays, setSelectedDays] = useState<number[]>([]);
    const [selectAll, setSelectAll] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setSelectedDays([]);
            setSelectAll(false);
        }
    }, [isOpen]);

    const handleSelectAll = (checked: boolean) => {
        setSelectAll(checked);
        if (checked) {
            const allDays = days
                .map((_, index) => index)
                .filter((index) => index !== sourceDayIndex);
            setSelectedDays(allDays);
        } else {
            setSelectedDays([]);
        }
    };

    const handleDayToggle = (dayIndex: number, checked: boolean) => {
        if (checked) {
            setSelectedDays([...selectedDays, dayIndex]);
        } else {
            setSelectedDays(selectedDays.filter((d) => d !== dayIndex));
            setSelectAll(false);
        }
    };

    const handleApply = () => {
        onCopy(selectedDays);
        setIsOpen(false);
    };

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                {children}
            </PopoverTrigger>
            <PopoverContent className="w-[300px] p-[20px]">
                <div className="flex flex-col gap-[15px]">
                    <h3 className="text-theme-0 font-semibold text-base">Copy times to</h3>
                    <div className="flex items-center gap-[10px]">
                        <Checkbox
                            checked={selectAll}
                            onCheckedChange={handleSelectAll}
                        />
                        <label className="text-theme-0 font-medium text-sm cursor-pointer" onClick={() => handleSelectAll(!selectAll)}>Select All</label>
                    </div>
                    <Separator />
                    <div className="flex flex-col gap-[10px] max-h-[250px] overflow-y-auto">
                        {days.map((day, index) => {
                            if (index === sourceDayIndex) return null;
                            return (
                                <div key={index} className="flex items-center gap-[10px]">
                                    <Checkbox
                                        checked={selectedDays.includes(index)}
                                        onCheckedChange={(checked) => handleDayToggle(index, checked as boolean)}
                                    />
                                    <label className="text-theme-0 text-sm cursor-pointer" onClick={() => handleDayToggle(index, !selectedDays.includes(index))}>{day}</label>
                                </div>
                            );
                        })}
                    </div>
                    <div className="flex justify-end gap-[10px] pt-[10px]">
                        <button onClick={() => setIsOpen(false)} className="px-[15px] py-[6px] border border-theme rounded-md text-theme-0 text-sm hover:bg-theme-1 transition-colors">
                            Cancel
                        </button>
                        <button onClick={handleApply} className="px-[15px] py-[6px] bg-theme-0 rounded-md text-theme-5 text-sm hover:opacity-90 transition-opacity">
                            Apply
                        </button>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
};

const ScheduleEditorTimes = ({ schedule, courseId, back, onScheduleChange }: { schedule: SchedulePeriod[], courseId: string, back: () => void, onScheduleChange: () => void }) => {

    const [times, setTimes] = useState<SchedulePeriod[]>(schedule || []);

    const { updateOptions, getOptions } = useCommunication();

    const updateSchedule = (newTimes: SchedulePeriod[]) => {
        setTimes(newTimes);
        const key = "card_" + courseId;
        const current = getOptions()?.[key] || {};
        updateOptions({ [key]: { ...current, schedule: newTimes } });
        onScheduleChange();
    }

    const modifyTimes = (index: number, changes: any) => {
        const newTimes = times.map((time: SchedulePeriod, i: number) => {
            if (i === index) {
                return { ...time, ...changes };
            }
            return time;
        });
        setTimes(newTimes);
        updateSchedule(newTimes);
    }

    const addTime = (day: number) => {
        const newTimes = [...times, { day, start: 780, end: 840 }];
        setTimes(newTimes);
        updateSchedule(newTimes);
    }

    const addTimeSlot = (day: number) => {
        const existingSlots = times.filter((time: SchedulePeriod) => time.day === day);
        let start = 780;
        let end = 840;

        if (existingSlots.length > 0) {
            const lastSlot = existingSlots[existingSlots.length - 1];
            start = lastSlot.end;
            end = Math.min(lastSlot.end + 60, 1440);
        }

        const newTimes = [...times, { day, start, end }];
        setTimes(newTimes);
        updateSchedule(newTimes);
    }

    const removeTime = (day: number) => {
        const newTimes = times.filter((time: SchedulePeriod) => time.day !== day);
        setTimes(newTimes);
        updateSchedule(newTimes);
    }

    const removeTimeSlot = (index: number) => {
        const newTimes = times.filter((time: SchedulePeriod, i: number) => i !== index);
        setTimes(newTimes);
        updateSchedule(newTimes);
    }

    const copyTimesToDays = (sourceDayIndex: number, targetDays: number[]) => {
        const sourceSlots = times.filter((time: SchedulePeriod) => time.day === sourceDayIndex);
        const slotsToAdd = targetDays.flatMap((targetDay) =>
            sourceSlots.map((slot) => ({ ...slot, day: targetDay }))
        );
        const newTimes = [...times, ...slotsToAdd];
        setTimes(newTimes);
        updateSchedule(newTimes);
    }

    const renderRanges = (day: number) => {
        const daySlots = times
            .map((time: SchedulePeriod, index: number) => ({ ...time, originalIndex: index }))
            .filter((time) => time.day === day);

        const showTrash = daySlots.length > 1;

        return daySlots.map((time) => {
            return (
                <ScheduleTimeRange
                    key={time.originalIndex}
                    initStart={time.start}
                    initEnd={time.end}
                    handleChange={(changes: any) => modifyTimes(time.originalIndex, changes)}
                    handleDelete={() => removeTimeSlot(time.originalIndex)}
                    showTrash={showTrash}
                />
            );
        });
    }

    const handleChange = (value: boolean, day: number) => {
        if (value) {
            addTime(day);
        } else {
            removeTime(day);
        }
    }

    const renderDays = useMemo(() => {
        return days.map((day: string, index: number) => {
            const hasTimes = times.some((time: SchedulePeriod) => time.day === index);

            return (
                <div key={day}>
                    <div className="flex items-center gap-[10px]">
                        <Switch height={20} checked={hasTimes} onCheckedChange={(value: boolean) => handleChange(value, index)} />
                        <p className="font-medium text-theme-0 min-w-[120px]">{day}</p>
                        <div className="flex items-center gap-[10px] flex-wrap">
                            {renderRanges(index)}
                        </div>
                        {hasTimes && (
                            <div className="flex items-center gap-[5px]">
                                <button
                                    className="rounded-md p-[5px] hover:bg-theme-2 transition-colors"
                                    onClick={() => addTimeSlot(index)}
                                    title="Add new time slot"
                                >
                                    <Plus className="h-[16px] w-[16px] text-theme-0" />
                                </button>
                                <CopyTimesPopover
                                    sourceDayIndex={index}
                                    onCopy={(targetDays) => copyTimesToDays(index, targetDays)}
                                >
                                    <button
                                        className="rounded-md p-[5px] hover:bg-theme-2 transition-colors"
                                        title="Copy times to other days"
                                    >
                                        <Copy className="h-[16px] w-[16px] text-theme-0" />
                                    </button>
                                </CopyTimesPopover>
                            </div>
                        )}
                    </div>
                </div>
            )
        })
    }, [times]);

    return (
        <div className="flex flex-col gap-[20px]">
            {renderDays}
        </div>
    )
}
const ScheduleEditor = ({ initialCourseId }: { initialCourseId?: string | null }) => {
    const [activeCourse, setActiveCourse] = useState<string | null>(initialCourseId || null);
    const [scheduleChanged, setScheduleChanged] = useState(false);
    const { courseData } = useCourses();

    const { trackEvent, withEventCooldown, googleCalendar, microsoftCalendar } = useCommunication();

    // Call refreshClassTimes on unmount only if schedule changes were made
    useEffect(() => {
        return () => {
            if (scheduleChanged) {
                googleCalendar.refreshClassTimes();
                microsoftCalendar.refreshClassTimes();
            }
        }
    }, [scheduleChanged]);

    useEffect(() => {
        withEventCooldown("schedule_editor_opened", async () => {
            await trackEvent("schedule_editor_opened");
        });
    }, []);

    useEffect(() => {
        if (initialCourseId) setActiveCourse(initialCourseId);
    }, [initialCourseId]);

    const { scheduledCourses, unscheduledCourses } = useMemo(() => {
        return Object.keys(courseData).reduce((acc: { scheduledCourses: ReactElement[], unscheduledCourses: ReactElement[] }, id: any) => {
            const course = courseData[id];
            const courseElement = <ScheduleEditorCourse key={id} course={course} onClick={() => setActiveCourse(id)} />;

            if (course?.schedule && course.schedule.length > 0) {
                acc.scheduledCourses.push(courseElement);
            } else {
                acc.unscheduledCourses.push(courseElement);
            }

            return acc;
        }, { scheduledCourses: [], unscheduledCourses: [] });
    }, [courseData]);


    const handleBack = () => {
        setActiveCourse(null);
    }

    const renderHeader = () => {

        if (!activeCourse) {
            return (
                <div className="flex flex-col gap-[5px]">
                    <h3 className="text-xl font-semibold text-theme-0 flex items-center gap-[5px] cursor-pointer">
                        <span>Class schedules</span>
                    </h3>
                    <p className="text-sm text-theme-1">Add your class times here to block them on your calendar</p>
                </div>
            )
        }

        const selectedCourseData = courseData[activeCourse];

        return (
            <div className="flex flex-col gap-[5px]">
                <h3 className="text-xl font-semibold text-theme-0 flex items-center gap-[10px] cursor-pointer" onClick={handleBack}>
                    <ChevronLeft className="h-[24px] w-[24px]" />
                    <span>Class schedules</span>
                </h3>
                <div className="flex items-center gap-[10px]">
                    <div className="size-[12px] rounded-full" style={{ backgroundColor: selectedCourseData.color }}></div>
                    <h2 className="font-semibold text-theme-0">{selectedCourseData.code !== "" ? selectedCourseData.code : selectedCourseData.default}</h2>
                </div>
            </div>
        )
    }

    return (
        <div className="w-[600px] h-[600px] p-[20px] flex flex-col">
            <div className="flex items-center gap-[10px] flex-shrink-0">
                {renderHeader()}
            </div>
            <div className="flex flex-col gap-[10px] mt-[20px] flex-1 min-h-0">
                <ScrollArea className="h-full w-full">
                    <div className="pr-[15px] max-w-[475px]">
                        {!activeCourse && unscheduledCourses.length > 0 && (
                            <div className="flex flex-col gap-[10px]">
                                <h3 className="font-semibold text-theme-0">Unscheduled</h3>
                                {unscheduledCourses}
                            </div>
                        )}

                        {!activeCourse && scheduledCourses.length > 0 && (
                            <>
                                <Separator className="my-[20px]" />
                                <div className="flex flex-col gap-[10px]">
                                    <h3 className="font-semibold text-theme-0">Scheduled</h3>
                                    {scheduledCourses}
                                </div>
                            </>
                        )}
                        {activeCourse && <ScheduleEditorTimes back={() => setActiveCourse(null)} schedule={courseData[activeCourse]?.schedule} courseId={activeCourse} onScheduleChange={() => setScheduleChanged(true)} />}
                    </div>
                </ScrollArea>
            </div>
        </div>
    )
}

export { ScheduleEditor };
